/**
 * SPA-side audit-event helpers for `MaskedField`.
 *
 * The initial reveal is **fail-closed**: `revealAudit` resolves with a
 * `{ ok: true }` result only when the BFF returns 2xx. On any non-2xx /
 * network failure it resolves with `{ ok: false }` carrying the correlation
 * id (if any) so the MaskedField can surface it in its inline error.
 *
 * The auto-re-mask audit is **fire-and-forget**: `remaskAudit` swallows
 * any failure (logging a warning to the console) because the value has
 * already been hidden in the DOM and re-mask is the after-the-fact
 * disposal record - it must not block the user.
 *
 * Both helpers re-raise `BffError` only for the unauthorised / forbidden
 * cases that the shared `BffErrorProvider` needs to route on; the caller
 * passes them straight to `useBffError().handle`.
 */
import {
  BffError,
  type BffClient,
  type PiiField,
  type PiiResource,
  type PiiRevealAuditReason,
} from './bff-client'

export interface RevealAuditOk {
  readonly ok: true
  readonly correlationId: string | null
}

export interface RevealAuditFail {
  readonly ok: false
  readonly correlationId: string | null
  readonly status: number
  readonly kind: BffError['kind']
  /** Set when 401/403 - the caller should forward to BffErrorProvider. */
  readonly authError: BffError | null
}

export type RevealAuditResult = RevealAuditOk | RevealAuditFail

export async function revealAudit(
  client: BffClient,
  accountId: string,
  field: PiiField,
  resource: PiiResource = 'account',
): Promise<RevealAuditResult> {
  try {
    // Only thread `resource` for holder reveals; account reveals keep the
    // exact `{ accountId, field }` shape (PR-E2) so the network body and
    // the call contract are byte-identical to before SM2-7.
    const args = resource === 'holder' ? { accountId, field, resource } : { accountId, field }
    const { correlationId } = await client.postPiiRevealed(args)
    return { ok: true, correlationId }
  } catch (err) {
    if (err instanceof BffError) {
      const authError = err.kind === 'unauthorised' || err.kind === 'forbidden' ? err : null
      return {
        ok: false,
        correlationId: err.correlationId,
        status: err.status,
        kind: err.kind,
        authError,
      }
    }
    // Non-BffError - treat as network/unknown failure.
    return {
      ok: false,
      correlationId: null,
      status: 0,
      kind: 'network',
      authError: null,
    }
  }
}

/**
 * Fire-and-forget auto-re-mask audit. The promise never rejects; a
 * failure is logged via `console.warn` so an oncall can correlate it with
 * BFF-side audit-emit-failure metrics, but the SPA never blocks on it
 * (the value has already been re-masked in the DOM).
 */
export function remaskAudit(
  client: BffClient,
  accountId: string,
  field: PiiField,
  reason: Exclude<PiiRevealAuditReason, 'initial_reveal'>,
  resource: PiiResource = 'account',
): void {
  const args =
    resource === 'holder' ? { accountId, field, reason, resource } : { accountId, field, reason }
  void client.postPiiRevealed(args).catch((err: unknown) => {
    const message = err instanceof Error ? err.message : 'unknown'
    // Intentional console.warn (NOT error): re-mask audit is fire-and-forget
    // per the PR-E2 design. The metric_name keeps the log line greppable.
    console.warn('[murmuration] re-mask audit failed (fire-and-forget):', {
      metric_name: 'murmuration_remask_audit_failure_total',
      field,
      reason,
      message,
    })
  })
}
